import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePacking } from '../../hooks/usePacking';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { 
  Plus, 
  Calendar, 
  MapPin, 
  Users,
  Package,
  CheckCircle,
  Clock,
  Sparkles,
  Luggage,
  Sun,
  Cloud,
  CloudRain,
  Snowflake,
  Wind,
  Archive,
  ArrowLeft,
  X,
  Settings
} from 'lucide-react';
import { format, differenceInDays, isAfter, addWeeks } from 'date-fns';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { weatherService } from '../../lib/weather-service';

export default function PackingDashboard() {
  const { lists, loading, createList, updateList, deleteList, templates } = usePacking();
  const { currentUser } = useFamilyContext();
  const navigate = useNavigate();
  const currentMember = currentUser; // For consistency
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filter, setFilter] = useState('active'); // active, upcoming, archived, all
  const [ownershipFilter, setOwnershipFilter] = useState('all'); // own, all

  // Filter lists based on both status and ownership filters
  const filteredLists = lists.filter(list => {
    // First apply ownership filter
    const isOwned = list.owner_id === currentMember?.id;
    if (ownershipFilter === 'own' && !isOwned) {
      return false;
    }

    // Then apply status filter
    const now = new Date();
    const twoWeeksFromNow = addWeeks(now, 2);
    const listStartDate = list.start_date ? new Date(list.start_date + 'T00:00:00') : null;

    if (filter === 'active') {
      // Active: trips within the next 2 weeks OR lists that are currently being packed
      if (list.status === 'packing' || list.status === 'packed') {
        return true;
      }
      if (!listStartDate) {
        return list.status === 'planning';
      }
      return listStartDate <= twoWeeksFromNow;
    } else if (filter === 'upcoming') {
      // Upcoming: trips more than 2 weeks away
      if (!listStartDate) return false;
      return isAfter(listStartDate, twoWeeksFromNow);
    } else if (filter === 'archived') {
      return list.status === 'archived';
    }
    return true; // 'all' filter
  });

  console.log('🎒 Total lists:', lists.length, 'Filtered lists:', filteredLists.length);

  // Get weather icon
  const getWeatherIcon = (weather) => {
    switch(weather) {
      case 'hot': return <Sun className="w-5 h-5 text-orange-500" />;
      case 'warm': return <Sun className="w-5 h-5 text-yellow-500" />;
      case 'mild': return <Cloud className="w-5 h-5 text-stone-500" />;
      case 'cold': return <Snowflake className="w-5 h-5 text-azure-500" />;
      case 'rainy': return <CloudRain className="w-5 h-5 text-azure-600" />;
      case 'mixed': return <Wind className="w-5 h-5 text-slate-600" />;
      default: return null;
    }
  };

  // Get trip type icon
  const getTripTypeIcon = (type) => {
    const icons = {
      vacation: Luggage,
      business: Package,
      camping: Package,
      beach: Luggage,
      winter_sports: Luggage,
      international: Luggage,
      weekend: Package,
      other: Luggage
    };
    const IconComponent = icons[type] || Luggage;
    return <IconComponent className="w-5 h-5" />;
  };

  // Calculate packing progress
  const getPackingProgress = (list) => {
    if (!list.items || list.items.length === 0) return 0;
    const packedCount = list.items.filter(item => item.packed).length;
    return Math.round((packedCount / list.items.length) * 100);
  };

  // Get days until trip
  const getDaysUntilTrip = (startDate) => {
    if (!startDate) return null;
    const days = differenceInDays(new Date(startDate + 'T00:00:00'), new Date());
    if (days < 0) return 'In progress';
    if (days === 0) return 'Today!';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500 mx-auto mb-4"></div>
          <p className="text-stone-600 font-medium">Loading packing lists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header - Consistent with other pages */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* Color bar */}
                <div className="w-2 h-6 bg-amber-500 rounded-full"></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 tracking-tight">Packing Lists</h1>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Trip</span>
            </button>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div className="px-4 pb-4 space-y-3">
          {/* Status Filter */}
          <div>
            <label className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2 block">Status</label>
            <div className="flex gap-2">
              {['active', 'upcoming', 'archived', 'all'].map(filterType => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`px-3 py-2 rounded-lg font-medium capitalize transition-all text-sm ${
                    filter === filterType
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {filterType}
                </button>
              ))}
            </div>
          </div>

          {/* Ownership Filter */}
          <div>
            <label className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2 block">Lists</label>
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'All Lists' },
                { key: 'own', label: 'My Lists' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setOwnershipFilter(key)}
                  className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                    ownershipFilter === key
                      ? 'bg-coral-100 text-coral-800 border border-coral-200'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Mobile New Trip Button */}
        <div className="md:hidden px-4 pb-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>New Trip</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">
        {/* Empty State */}
        {filteredLists.length === 0 ? (
          <div className="bg-white rounded-xl p-8 border border-stone-200 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Luggage className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {filter === 'archived' ? 'No archived trips yet!' : 'Ready for your next adventure?'}
            </h3>
            <p className="text-stone-600 mb-6">
              Create your first packing list!
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-coral-500 hover:bg-coral-600 text-white font-medium rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Plan Your Trip
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredLists.map(list => {
              const progress = getPackingProgress(list);
              const daysUntil = getDaysUntilTrip(list.start_date);
              const isUpcoming = list.start_date && isAfter(new Date(list.start_date + 'T00:00:00'), new Date());

              return (
                <Link
                  key={list.id}
                  to={`/packing/${list.id}`}
                  className="block bg-white rounded-xl border border-stone-200 overflow-hidden hover:border-stone-300 hover:shadow-sm transition-all no-underline min-h-[280px] flex flex-col"
                >
                  {/* Header with trip type color */}
                  <div className={`p-4 ${
                    list.trip_type === 'vacation' ? 'bg-gradient-to-r from-emerald-500 to-cyan-700' :
                    list.trip_type === 'business' ? 'bg-gradient-to-r from-slate-600 to-slate-700' :
                    list.trip_type === 'camping' ? 'bg-gradient-to-r from-emerald-400 to-emerald-700' :
                    list.trip_type === 'beach' ? 'bg-gradient-to-r from-cyan-500 to-cyan-600' :
                    'bg-gradient-to-r from-coral-500 to-coral-600'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-white flex-shrink-0">{getTripTypeIcon(list.trip_type)}</div>
                          <h3 className="text-lg font-semibold text-white truncate">
                            {list.name}
                          </h3>
                          {list.owner_id !== currentMember?.id && (
                            <span className="text-xs bg-white/20 text-white px-2 py-1 rounded-full flex-shrink-0">
                              Shared
                            </span>
                          )}
                        </div>
                        {list.destination && (
                          <div className="flex items-center gap-2 text-white/90 text-sm">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{list.destination}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {getWeatherIcon(list.weather_expected)}
                      </div>
                    </div>

                    {/* Status badges */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        list.status === 'planning' ? 'bg-white/20 text-white' :
                        list.status === 'packing' ? 'bg-yellow-100 text-yellow-800' :
                        list.status === 'packed' ? 'bg-emerald-100 text-emerald-800' :
                        'bg-stone-100 text-slate-800'
                      }`}>
                        {list.status}
                      </span>
                      {isUpcoming && daysUntil && (
                        <span className="px-2 py-1 rounded-full bg-white/20 text-white text-xs font-medium">
                          {daysUntil}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    {/* Date range */}
                    {list.start_date && (
                      <div className="flex items-center gap-2 text-stone-600 text-sm mb-3">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(list.start_date + 'T00:00:00'), 'MMM d')}
                        {list.end_date && ` - ${format(new Date(list.end_date + 'T00:00:00'), 'MMM d, yyyy')}`}
                      </div>
                    )}

                    {/* Progress */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-600">Packing Progress</span>
                        <span className="font-medium text-slate-900">{progress}%</span>
                      </div>
                      <div className="w-full bg-stone-200 rounded-full h-2">
                        <div 
                          className="bg-coral-500 h-full rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="text-center p-3 bg-stone-50 rounded-lg">
                        <div className="text-xl font-semibold text-slate-900">
                          {list.items?.length || 0}
                        </div>
                        <div className="text-xs text-stone-600">Items</div>
                      </div>
                      <div className="text-center p-3 bg-coral-50 rounded-lg">
                        <div className="text-xl font-semibold text-coral-700">
                          {list.items?.filter(item => item.packed).length || 0}
                        </div>
                        <div className="text-xs text-stone-600">Packed</div>
                      </div>
                    </div>

                    {/* Collaborators */}
                    {list.collaborators && list.collaborators.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-stone-600">
                        <Users className="w-4 h-4" />
                        <div className="flex -space-x-1">
                          {list.collaborators.slice(0, 3).map(collab => {
                            // Debug log to see the collaborator data structure
                            console.log('🖼️ Dashboard collaborator data:', {
                              collab_id: collab.id,
                              collaborator_id: collab.collaborator_id,
                              collaborator: collab.collaborator
                            });
                            
                            // Try multiple possible name fields from family_members table
                            const displayName = collab.collaborator?.display_name || 
                                               collab.collaborator?.name || 
                                               'Unknown';
                            
                            return (
                              <AvatarOrInitial
                                key={collab.id}
                                avatar={collab.collaborator?.avatar}
                                name={displayName}
                                user={collab.collaborator}
                                className="w-6 h-6 border-2 border-white"
                              />
                            );
                          })}
                          {list.collaborators.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-stone-300 border-2 border-white flex items-center justify-center text-xs font-medium">
                              +{list.collaborators.length - 3}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Template indicator */}
                    {list.template && (
                      <div className="flex items-center gap-2 text-xs text-violet-600 mt-3">
                        <Sparkles className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">From template: {list.template.name}</span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreatePackingListModal
          onClose={() => setShowCreateModal(false)}
          onCreateList={createList}
          templates={templates}
        />
      )}
    </div>
  );
}

// Create Modal Component
function CreatePackingListModal({ onClose, onCreateList, templates }) {
  const [step, setStep] = useState(1); // 1: Choose method, 2: Template selection, 3: Details
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    destination: '',
    zip_code: '',
    trip_type: 'vacation',
    start_date: '',
    end_date: '',
    weather_expected: 'mild',
    transportation_mode: 'car'
  });
  
  const [tripLegs, setTripLegs] = useState([
    {
      id: 1,
      location: '',
      zip_code: '',
      start_date: '',
      end_date: '',
      weather_data: null
    }
  ]);
  
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Handle Esc key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Look up weather when zip code and dates are provided
  const lookupWeather = async () => {
    if (!formData.zip_code || !formData.start_date || !formData.end_date) {
      return;
    }

    setWeatherLoading(true);
    try {
      const weather = await weatherService.getWeatherForTrip(
        formData.zip_code,
        formData.start_date,
        formData.end_date
      );
      
      if (weather) {
        setWeatherData(weather);
        const suggestions = weatherService.getTripPackingSuggestions(weather);
        
        // Auto-set weather category
        setFormData(prev => ({
          ...prev,
          weather_expected: suggestions.weatherCategory
        }));
      }
    } catch (error) {
      console.error('Failed to lookup weather:', error);
    } finally {
      setWeatherLoading(false);
    }
  };

  // Trip legs management
  const addTripLeg = () => {
    const newId = Math.max(...tripLegs.map(leg => leg.id)) + 1;
    setTripLegs([...tripLegs, {
      id: newId,
      location: '',
      zip_code: '',
      start_date: '',
      end_date: '',
      weather_data: null
    }]);
  };

  const removeTripLeg = (legId) => {
    if (tripLegs.length > 1) {
      setTripLegs(tripLegs.filter(leg => leg.id !== legId));
    }
  };

  const updateTripLeg = (legId, field, value) => {
    setTripLegs(tripLegs.map(leg => 
      leg.id === legId ? { ...leg, [field]: value } : leg
    ));
  };

  // Lookup weather for a specific trip leg
  const lookupLegWeather = async (legId) => {
    const leg = tripLegs.find(l => l.id === legId);
    if (!leg || !leg.zip_code || !leg.start_date || !leg.end_date) return;

    try {
      const weather = await weatherService.getWeatherForTrip(
        leg.zip_code,
        leg.start_date,
        leg.end_date
      );
      
      if (weather) {
        setTripLegs(tripLegs.map(l => 
          l.id === legId ? { ...l, weather_data: weather } : l
        ));
      }
    } catch (error) {
      console.error('Failed to lookup weather for leg:', error);
    }
  };

  // Auto-lookup weather when zip code and dates change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.zip_code && formData.start_date && formData.end_date) {
        lookupWeather();
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timer);
  }, [formData.zip_code, formData.start_date, formData.end_date]);

  // Auto-lookup weather for trip legs
  useEffect(() => {
    const timer = setTimeout(() => {
      tripLegs.forEach(leg => {
        if (leg.zip_code && leg.start_date && leg.end_date && !leg.weather_data) {
          lookupLegWeather(leg.id);
        }
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [tripLegs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const listData = {
        ...formData,
        weather_data: weatherData, // Include main weather data
        trip_legs: tripLegs.filter(leg => leg.location && leg.zip_code) // Include trip legs with data
      };
      
      if (selectedTemplate) {
        // Create from template
        await onCreateList({
          ...listData,
          template_id: selectedTemplate.id
        });
      } else {
        // Create blank list
        await onCreateList(listData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-stone-200 shadow-lg">
        {/* Header */}
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900">
              {step === 1 ? 'Create New Packing List' :
               step === 2 ? 'Choose a Template' :
               'Trip Details'}
            </h2>
            <button
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <button
                onClick={() => setStep(3)}
                className="w-full p-4 bg-coral-50 rounded-lg border border-coral-200 hover:bg-coral-100 transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-coral-500 rounded-lg flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Start from Scratch</h3>
                    <p className="text-sm text-stone-600">Create a custom packing list</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setStep(2)}
                className="w-full p-4 bg-violet-50 rounded-lg border border-violet-200 hover:bg-violet-100 transition-all text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Use a Template</h3>
                    <p className="text-sm text-stone-600">Start with pre-made lists</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-stone-600 mb-4">
                Choose a template that matches your trip type:
              </p>
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setStep(3);
                  }}
                  className="w-full p-4 bg-white rounded-lg border border-stone-200 hover:border-coral-300 hover:bg-stone-50 transition-all text-left"
                >
                  <h4 className="font-semibold text-slate-900">{template.name}</h4>
                  <p className="text-sm text-stone-600">{template.description}</p>
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Trip Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Summer vacation, Business trip to NYC..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Destination
                  </label>
                  <input
                    type="text"
                    value={formData.destination}
                    onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    placeholder="Paris, France"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Zip Code
                    <span className="text-xs text-stone-500 ml-1">(for weather)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    placeholder="27606"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Trip Type
                </label>
                <select
                  value={formData.trip_type}
                  onChange={(e) => setFormData({...formData, trip_type: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                >
                  <option value="vacation">Vacation</option>
                  <option value="business">Business</option>
                  <option value="camping">Camping</option>
                  <option value="beach">Beach</option>
                  <option value="winter_sports">Winter Sports</option>
                  <option value="international">International</option>
                  <option value="weekend">Weekend</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Expected Weather
                  {weatherLoading && <span className="text-xs text-azure-600 ml-2">Looking up weather...</span>}
                </label>
                
                {weatherData && (
                  <div className="mb-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                    <div className="text-sm text-azure-900">
                      <strong>{weatherData.cityName}</strong>
                      {weatherData.isCurrentForecast ? (
                        <span className="text-emerald-600 ml-2">
                          ✓ Forecast available for trip dates
                          {weatherData.forecast && ` (${weatherData.forecast.length} days)`}
                        </span>
                      ) : (
                        <span className="text-amber-600 ml-2">
                          ⚠ {weatherData.message}
                        </span>
                      )}
                    </div>
                    {weatherData.forecast && weatherData.forecast.length > 0 && (
                      <div className="text-xs text-azure-700 mt-1">
                        Temps: {Math.min(...weatherData.forecast.map(d => d.tempMin))}°F - {Math.max(...weatherData.forecast.map(d => d.tempMax))}°F
                      </div>
                    )}
                    {weatherData.currentWeather && (
                      <div className="text-xs text-azure-700 mt-1">
                        Current: {weatherData.currentWeather.temp}°F, {weatherData.currentWeather.description}
                      </div>
                    )}
                  </div>
                )}
                
                <select
                  value={formData.weather_expected}
                  onChange={(e) => setFormData({...formData, weather_expected: e.target.value})}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                >
                  <option value="hot">Hot</option>
                  <option value="warm">Warm</option>
                  <option value="mild">Mild</option>
                  <option value="cold">Cold</option>
                  <option value="rainy">Rainy</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>

              {/* Advanced Trip Legs Section */}
              <div className="border-t border-stone-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Advanced: Multiple Locations
                  {showAdvanced ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </button>
                
                {showAdvanced && (
                  <div className="mt-4 space-y-4">
                    <div className="text-sm text-stone-600 mb-3">
                      Add multiple locations if your trip involves different cities (each location will have separate weather forecasts).
                    </div>
                    
                    {tripLegs.map((leg, index) => (
                      <div key={leg.id} className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-slate-900">
                            Location {index + 1}
                          </h5>
                          {tripLegs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeTripLeg(leg.id)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Location name"
                            value={leg.location}
                            onChange={(e) => updateTripLeg(leg.id, 'location', e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                          />
                          <input
                            type="text"
                            placeholder="Zip code"
                            value={leg.zip_code}
                            onChange={(e) => updateTripLeg(leg.id, 'zip_code', e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                          />
                          <input
                            type="date"
                            value={leg.start_date}
                            onChange={(e) => updateTripLeg(leg.id, 'start_date', e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                          />
                          <input
                            type="date"
                            value={leg.end_date}
                            onChange={(e) => updateTripLeg(leg.id, 'end_date', e.target.value)}
                            className="px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                          />
                        </div>
                        
                        {leg.weather_data && (
                          <div className="mt-3 p-3 bg-azure-50 border border-azure-200 rounded-lg">
                            <div className="text-sm text-azure-900">
                              <strong>{leg.weather_data.cityName}</strong>
                              {leg.weather_data.isCurrentForecast ? (
                                <span className="text-emerald-600 ml-2">
                                  ✓ Forecast available
                                </span>
                              ) : (
                                <span className="text-amber-600 ml-2">
                                  ⚠ {leg.weather_data.message}
                                </span>
                              )}
                            </div>
                            {leg.weather_data.forecast && leg.weather_data.forecast.length > 0 && (
                              <div className="text-xs text-azure-700 mt-1">
                                Temps: {Math.min(...leg.weather_data.forecast.map(d => d.tempMin))}°F - {Math.max(...leg.weather_data.forecast.map(d => d.tempMax))}°F
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={addTripLeg}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-coral-600 hover:text-coral-700 hover:bg-coral-50 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Location
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                  className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  {step === 1 ? 'Cancel' : 'Back'}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-coral-500 text-white py-2 px-4 rounded-lg hover:bg-coral-600 transition-colors"
                >
                  Create Packing List
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}